import React, { useEffect, useState } from "react";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { SectionHeader, StatCard, Loader, Lu, CHART_COLORS, fmtSAR } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function SuperAdmin() {
  const { t, lang } = useLang();
  const [billing, setBilling] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [hardware, setHardware] = useState([]);
  const [support, setSupport] = useState([]);

  useEffect(() => {
    Promise.all([
      client.get("/admin/billing"), client.get("/admin/tenants"),
      client.get("/admin/hardware"), client.get("/admin/support"),
    ]).then(([b, tn, h, s]) => { setBilling(b.data); setTenants(tn.data); setHardware(h.data); setSupport(s.data); });
  }, []);

  if (!billing) return <Loader />;
  const badge = (c, txt) => <span className="text-xs px-2 py-0.5 rounded-full" style={{ color: c, background: `${c}1a` }}>{txt}</span>;
  const sc = { open: "#F59E0B", in_progress: "#3B82F6", resolved: "#10B981", paid: "#10B981", pending: "#F59E0B", active: "#10B981" };
  const pc = { high: "#F43F5E", medium: "#F59E0B", low: "#64748B" };

  return (
    <div>
      <SectionHeader icon="shield-check" title={t("superAdmin")} subtitle="Madarek Internal · Multi-tenant" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <StatCard icon="wallet" label={t("totalMrr")} value={`${fmtSAR(billing.total_mrr)}`} sub={t("sar")} accent={CHART_COLORS.gold} testid="mrr" />
        <StatCard icon="trending-up" label={t("totalArr")} value={`${fmtSAR(billing.total_arr)}`} sub={t("sar")} accent={CHART_COLORS.emerald} />
        <StatCard icon="building-2" label={t("activeTenants")} value={billing.active_tenants} accent={CHART_COLORS.blue} />
        <StatCard icon="grid-3x3" label={t("activeRooms")} value={billing.active_rooms} accent={CHART_COLORS.amber} />
      </div>

      <Tabs defaultValue="tenants">
        <TabsList className="bg-foreground/5 border border-border mb-5">
          {[["tenants", "tenants"], ["billing", "billing"], ["hardware", "hardware"], ["support", "support"]].map(([v, k]) => (
            <TabsTrigger key={v} value={v} data-testid={`sa-tab-${v}`}
              className="data-[state=active]:bg-primary data-[state=active]:shadow-md data-[state=active]:text-primary-foreground text-foreground/80">{t(k)}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="tenants">
          <div className="md-card p-1 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">{t("properties")}</TableHead><TableHead className="text-muted-foreground">Plan</TableHead>
                <TableHead className="text-muted-foreground">{t("totalRooms")}</TableHead><TableHead className="text-muted-foreground">MRR</TableHead>
                <TableHead className="text-muted-foreground">{t("status")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {tenants.map((tn) => (
                  <TableRow key={tn.id} className="border-border" data-testid={`tenant-${tn.id}`}>
                    <TableCell className="text-foreground font-medium">{tn[lang === "ar" ? "name_ar" : "name_en"]}</TableCell>
                    <TableCell>{badge(CHART_COLORS.gold, tn.plan)}</TableCell>
                    <TableCell className="text-foreground/80 font-mono">{tn.room_count}</TableCell>
                    <TableCell className="text-primary font-mono">{fmtSAR(tn.mrr)} {t("sar")}</TableCell>
                    <TableCell>{badge(sc.active, t("activeTenants").split(" ")[0])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="billing">
          <div className="md-card p-1 overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">#</TableHead><TableHead className="text-muted-foreground">{t("tenants")}</TableHead>
                <TableHead className="text-muted-foreground">{t("amount")}</TableHead><TableHead className="text-muted-foreground">{t("date")}</TableHead>
                <TableHead className="text-muted-foreground">{t("status")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {billing.invoices.map((inv) => (
                  <TableRow key={inv.id} className="border-border">
                    <TableCell className="text-muted-foreground font-mono text-xs">{inv.id}</TableCell>
                    <TableCell className="text-foreground">{inv.tenant}</TableCell>
                    <TableCell className="text-primary font-mono">{fmtSAR(inv.amount)} {t("sar")}</TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">{inv.date}</TableCell>
                    <TableCell>{badge(sc[inv.status], inv.status === "paid" ? t("paid") : "Pending")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="hardware">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {hardware.map((h) => (
              <div key={h.sku} className="md-card p-4" data-testid={`hw-${h.sku}`}>
                <div className="flex items-center justify-between mb-2">
                  <Lu name="cpu" className="w-5 h-5 text-primary" />
                  {h.golden_sample && <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/15 text-primary font-bold">GOLDEN</span>}
                </div>
                <p className="text-sm font-semibold text-foreground">{h[lang === "ar" ? "name_ar" : "name_en"]}</p>
                <p className="text-xs text-muted-foreground/70 font-mono">{h.sku}</p>
                <div className="flex justify-between mt-3 text-xs">
                  <span className="text-muted-foreground">{t("inStock")}: <b className="text-emerald-400 font-mono">{h.in_stock}</b></span>
                  <span className="text-muted-foreground">{t("deployed")}: <b className="text-blue-400 font-mono">{h.deployed}</b></span>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="support">
          <div className="space-y-3">
            {support.map((s) => (
              <div key={s.id} className="md-card p-4 flex flex-wrap items-center gap-4" data-testid={`sup-${s.id}`}>
                <Lu name="life-buoy" className="w-5 h-5 text-primary shrink-0" />
                <div className="flex-1 min-w-[160px]">
                  <p className="text-sm font-medium text-foreground">{s[lang === "ar" ? "subject_ar" : "subject_en"]}</p>
                  <p className="text-xs text-muted-foreground/70">{s.tenant} · {s.id}</p>
                </div>
                {badge(pc[s.priority], t(s.priority))}
                {badge(sc[s.status], s.status)}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
